/**
 * Aggregator van alle 7 chakra's + afgeleide helpers (deck-opbouw, lookups).
 */
import type { Baas, Chakra, Kaart, Origin } from './types';
import { chakra1 } from './chakra1';
import { chakra2 } from './chakra2';
import { chakra3 } from './chakra3';
import { chakra4 } from './chakra4';
import { chakra5 } from './chakra5';
import { chakra6 } from './chakra6';
import { chakra7 } from './chakra7';
import { PDF_EXTRA } from './pdfExtra';

/** Voegt de PDF-verrijkingskaarten (fase 8b) toe aan de kaartenlijst van een chakra. */
function metPdfExtra(c: Chakra): Chakra {
  const extra = PDF_EXTRA[c.header.nummer] ?? [];
  return extra.length ? { ...c, kaarten: [...c.kaarten, ...extra] } : c;
}

/** Geïndexeerd op chakra-nummer (1..7). */
export const CHAKRAS: Record<Origin, Chakra> = {
  1: metPdfExtra(chakra1),
  2: metPdfExtra(chakra2),
  3: metPdfExtra(chakra3),
  4: metPdfExtra(chakra4),
  5: metPdfExtra(chakra5),
  6: metPdfExtra(chakra6),
  7: metPdfExtra(chakra7),
};

/** Geordende lijst wortel→kroon (incl. PDF-verrijking, voor pad/codex/lookups). */
export const CHAKRA_VOLGORDE: Chakra[] = [CHAKRAS[1], CHAKRAS[2], CHAKRAS[3], CHAKRAS[4], CHAKRAS[5], CHAKRAS[6], CHAKRAS[7]];

/** Alle kaarten van alle chakra's, plat. */
export const ALLE_KAARTEN: Kaart[] = CHAKRA_VOLGORDE.flatMap((c) => c.kaarten);

/** Kaart-lookup op id. */
const KAART_OP_ID = new Map<string, Kaart>(ALLE_KAARTEN.map((k) => [k.id, k]));
export function kaartOpId(id: string): Kaart | undefined {
  return KAART_OP_ID.get(id);
}

/** Kaart-lookup op naam (zwaktes/combo's verwijzen op naam). */
const KAART_OP_NAAM = new Map<string, Kaart>(ALLE_KAARTEN.map((k) => [k.naam, k]));
export function kaartOpNaam(naam: string): Kaart | undefined {
  return KAART_OP_NAAM.get(naam);
}

/** De meesterschapskaart van een chakra (ch1–6; ch7 heeft er geen). */
export function meesterschapskaart(chakra: Origin): Kaart | undefined {
  return CHAKRAS[chakra].kaarten.find((k) => k.meesterschap);
}

/**
 * Bouwt de beschikbare kaartenpool voor een chakra (§1.2): de 20(+) eigen
 * kaarten + alle eerder ontgrendelde meesterschapskaarten (van chakra 1..N-1).
 * `ontgrendeld` = set van reeds verslagen eindbaas-chakra's.
 */
export function kaartenPoolVoor(chakra: Origin, ontgrendeld: Set<Origin>): Kaart[] {
  const eigen = CHAKRAS[chakra].kaarten;
  const meesters: Kaart[] = [];
  for (let n = 1 as number; n < chakra; n++) {
    if (ontgrendeld.has(n as Origin)) {
      const m = meesterschapskaart(n as Origin);
      if (m) meesters.push(m);
    }
  }
  return [...eigen, ...meesters];
}

/** De 4 bazen van een chakra (3 sub + 1 eindbaas). */
export function bazenVan(chakra: Origin): Baas[] {
  return CHAKRAS[chakra].bazen;
}

/** De eindbaas van een chakra. */
export function eindbaasVan(chakra: Origin): Baas | undefined {
  return CHAKRAS[chakra].bazen.find((b) => b.eindbaas);
}
